import { useAppState, useBookingEngineStateContext } from "@app/app/hooks";
import { BodyScrollPreventer } from "@app/common/components/BodyScrollPreventer";
import React, { useEffect, useRef, useState, useTransition } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { twMerge } from "tailwind-merge";

import { AppZIndexes } from "../constants/AppZIndexes";
import { ErrorBoundaryProvider } from "../context/ErrorBoundaryProvider";
import { appElementsProvider } from "../services/appElements";
import { useTwind } from "../services/twind";
import { ErrorFallback } from "./ErrorFallback";
import { Widget } from "./Widget";
import { WidgetFloatingTrigger } from "./WidgetFloatingTrigger";

const backdropStyle: React.CSSProperties = {
    zIndex: AppZIndexes.Min,
};

export const AppWidgetsManager = () => {
    const [isRestoredWidgetStateApplied, setIsRestoredWidgetStateApplied] = useState(false);
    const [, startTransition] = useTransition();
    const { state: appState, isInitialized, requestWidgetTransition, widgetState } = useAppState();
    const { getState: getBookingEngineFormState } = useBookingEngineStateContext();
    const [initialRestorableStateValue, setInitialRestorableStateValue] = useState<string>();
    const [prevWidgetState, setPrevWidgetState] = useState<typeof widgetState>(widgetState);
    const triggerButtonRef = useRef<HTMLDivElement>(null);
    const widgetRef = useRef<HTMLDivElement>(null);
    const currentState = widgetState.name;
    const isStateVisible = currentState === "visible";
    const isStateHidden = currentState === "hidden" || currentState === "inactive" || currentState === "closed";
    const isPrevStateHidden =
        prevWidgetState.name === "visible" || prevWidgetState.name === "inactive" || prevWidgetState.name === "closed";
    const activeStep = getBookingEngineFormState().activeStep;

    const { tw } = useTwind();

    /**
     * Try to resolve previous ibe state from storage and set the value as the
     * value to restore to.
     * When no value could be found in storage, fallback to "visible" state.
     */
    useEffect(() => {
        if (isInitialized) {
            const storedWidgetState = appState.restoreableWidgetState || "hidden";

            if (storedWidgetState !== "hidden") {
                setInitialRestorableStateValue(storedWidgetState);
            }
        }
    }, [isInitialized, appState.restoreableWidgetState]);

    useEffect(() => {
        setPrevWidgetState(widgetState);
    }, [widgetState]);

    // Disable body scroll when the widget is visible
    // This to to prevent the body from scrolling when the widget is open
    // Needed to prevent other plugins/extensions from stealing the scroll event
    useEffect(() => {
        const preventBodyScroll = (e: TouchEvent | WheelEvent) => {
            e.stopPropagation();
        };

        if (isStateVisible) {
            document.addEventListener("wheel", preventBodyScroll, true);
            document.addEventListener("touchmove", preventBodyScroll, true);
        } else {
            document.removeEventListener("wheel", preventBodyScroll, true);
            document.removeEventListener("touchmove", preventBodyScroll, true);
        }

        return () => {
            document.removeEventListener("wheel", preventBodyScroll, true);
            document.removeEventListener("touchmove", preventBodyScroll, true);
        };
    }, [isStateVisible]);

    /**
     * This effect is responsible to restore the state of the widget based on the
     * restorable state value.
     * @note - when no restorableState value was found in storage, the default value "visible" is being used.
     *         But this is handled within the hook that is responsible to obtain the value from storage...
     */
    useEffect(() => {
        const isActiveStepThankYou = activeStep === "thank-you";

        if (!isRestoredWidgetStateApplied && (initialRestorableStateValue || isActiveStepThankYou) && isInitialized) {
            let event = "";
            // If the user is on thank-you page, the widget should be shown, but if the user closes the widget, it should stay closed
            if (isActiveStepThankYou && currentState !== "minimised") {
                event = "SHOW";
            } else if (initialRestorableStateValue === "visible") {
                event = "SHOW";
            } else if (initialRestorableStateValue === "minimised") {
                event = "MINIMISE";
            }

            if (event) {
                transitionAppState(event);
            }

            // RESTORED_WIDGET_STATE_APPLIED = true;
            setIsRestoredWidgetStateApplied(true);
        }
    }, [initialRestorableStateValue, isInitialized, currentState]);

    /**
     * Responsible to setIbeWidgetState on the appElementsProvider.
     * The appElementsProvider needs to know because it will set a matching data attribute
     * on the container element, which is used by the ws2 targeting to determine its state.
     *
     * It should be applied to an element outside the shadow DOM, so that the ws2 targeting can
     * make use of the query selector condition.
     */
    useEffect(() => {
        if (widgetState) {
            appElementsProvider.setIbeWidgetState(currentState);
        }
    }, [widgetState]);

    const handleWidgetCloseClick = () => {
        transitionAppState("MINIMISE");
    };

    const handleOpenClick = () => {
        transitionAppState("SHOW");
    };

    const handleCloseClick = async () => {
        transitionAppState("CLOSE");
    };

    const handleTransitionEnd = () => {
        // send("DONE");
    };

    const transitionAppState = (event: string) => {
        startTransition(() => {
            requestWidgetTransition(event);
        });
    };

    return (
        <div className={tw("overflow-hidden text-foreground")} data-hc-id="app-widgets-manager">
            <div
                className={tw(
                    twMerge(
                        "ease-in fixed top-0 right-0 w-full h-full",
                        currentState === "visible" ? "bg-black/40 pointer-events-auto" : "pointer-events-none",
                        isStateHidden ? "duration-0 transition-none" : "duration-500 transition-all"
                    )
                )}
                style={backdropStyle}
            ></div>

            <BodyScrollPreventer open={isStateVisible} />

            <ErrorBoundaryProvider>
                <ErrorBoundary FallbackComponent={ErrorFallback}>
                    <Widget
                        onTransitionEnd={handleTransitionEnd}
                        onCloseClick={handleWidgetCloseClick}
                        ref={widgetRef}
                        className={tw(
                            twMerge(
                                "ease-in-out",
                                currentState !== "visible"
                                    ? `translate-x-[${widgetRef.current?.offsetWidth || 800}px]`
                                    : "translate-x-[0px]",
                                currentState !== "visible" ? `right-[${-(widgetRef.current?.offsetWidth || 800)}px]` : "right-[0px]",
                                currentState === "minimised" ? "" : "delay-500",
                                isStateHidden ? "duration-0 transition-none" : "duration-500 transition-all"
                            )
                        )}
                        style={{
                            visibility: isStateHidden ? "hidden" : "visible",
                            zIndex: AppZIndexes.Ibe,
                        }}
                    />
                </ErrorBoundary>
            </ErrorBoundaryProvider>

            <div
                data-hc-id="ws2-root"
                className={tw(
                    twMerge(
                        "ease-in fixed",
                        currentState === "visible" ? "pointer-events-auto opacity-1" : "pointer-events-none opacity-0",
                        isStateHidden ? "duration-0 transition-none" : "duration-500 transition-all"
                    )
                )}
                style={{
                    zIndex: AppZIndexes.Ibe + 1,
                }}
            ></div>

            <WidgetFloatingTrigger
                onClickOpen={handleOpenClick}
                onClickClose={handleCloseClick}
                ref={triggerButtonRef}
                className={tw(
                    twMerge(
                        "drop-shadow-md ease-in-out",
                        currentState !== "minimised"
                            ? `translate-x-[${triggerButtonRef.current?.offsetWidth || 0}px]`
                            : "translate-x-[0px]",
                        currentState === "visible" ? "" : "delay-500",
                        isStateHidden || (isPrevStateHidden && initialRestorableStateValue !== "minimised")
                            ? "duration-0 transition-none"
                            : "duration-500 transition-all"
                    )
                )}
                style={{
                    visibility: isStateHidden || (isPrevStateHidden && initialRestorableStateValue !== "minimised") ? "hidden" : "visible",
                }}
            />
        </div>
    );
};
